/**
 * SEO HEADING STRUCTURE — Homepage (Behavioral Intelligence)
 * TITLE: Behavioral Intelligence for Student Success | CSW Study Stream
 * H1: The behavioral intelligence layer for education
 * H2: Institutions detect problems too late
 * H2: What CSW surfaces
 * H2: Already live at scale
 * H2: How CSW works
 * H2: Built for the teams supporting students
 * H2: Existing systems record. CSW understands.
 * H2: Designed to support — not replace — human judgment
 * H2: The student layer that powers the data
 */
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { InstLayout } from "@/components/institutional/InstLayout";
import { Button } from "@/components/ui/button";
import cswLogo from "@/assets/csw-logo-new.png";

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://csw.live/#organization",
      name: "CSW",
      url: "https://csw.live",
      logo: "https://csw.live/favicon.png",
      description:
        "Behavioral intelligence platform that surfaces early engagement and retention risk indicators for advisors and student success teams.",
    },
    {
      "@type": "WebPage",
      "@id": "https://csw.live/#webpage",
      url: "https://csw.live/",
      name: "Behavioral Intelligence for Student Success | CSW Study Stream",
      inLanguage: "en",
      isPartOf: { "@id": "https://csw.live/#organization" },
    },
    {
      "@type": "Service",
      name: "CSW Behavioral Intelligence",
      provider: { "@id": "https://csw.live/#organization" },
      serviceType: "Student success behavioral intelligence",
      areaServed: "Worldwide",
    },
  ],
};

const IndexB2B = () => {
  return (
    <InstLayout>
      <SEO
        title="Behavioral Intelligence for Student Success"
        description="CSW is the behavioral intelligence layer for education — surfacing early engagement and retention risk indicators for advisors and student success teams."
        keywords="behavioral intelligence, student success, engagement signals, retention risk, advising platform, higher education analytics"
        canonicalUrl="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden" aria-labelledby="hero-title">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(55% 45% at 85% 15%, hsl(193 95% 55% / 0.18), transparent 70%), radial-gradient(45% 40% at 12% 85%, hsl(328 85% 60% / 0.22), transparent 70%)",
          }}
        />
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="mb-5">
                <img
                  src={cswLogo}
                  alt="CSW"
                  className="h-14 sm:h-16 lg:h-20 w-auto"
                />
              </div>
              <h1
                id="hero-title"
                className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground mb-6"
              >
                See <em className="urgent">engagement</em> before it becomes a{" "}
                <span className="text-gradient not-italic">retention problem.</span>
              </h1>
              <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-xl mb-8">
                CSW analyzes behavioral learning patterns and academic context to surface early
                engagement and retention risk indicators — designed to support advisors and student
                success teams, never to replace human judgment.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-7 py-6 text-base glow-primary">
                  <a href="/institution/">For institutions<ArrowRight className="ml-2 h-4 w-4" /></a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-2 font-semibold px-7 py-6 text-base">
                  <a href="/students/">For students</a>
                </Button>
              </div>
              <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                Behavioral signals · Explainable insights · Human-in-the-loop
              </p>
            </div>

            <figure className="relative">
              <div className="relative rounded-3xl overflow-hidden border border-border/60 shadow-2xl">
                <video
                  src="/videos/productivity-demo.mp4"
                  autoPlay loop muted playsInline
                  className="w-full h-auto"
                  aria-label="CSW behavioral intelligence platform demo"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" aria-hidden="true" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs text-foreground/90 font-medium">
                  <span className="px-2.5 py-1 rounded-full bg-background/70 backdrop-blur-sm border border-border/60">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-secondary mr-1.5 align-middle animate-pulse" />
                    Live · Campus signal
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-background/70 backdrop-blur-sm border border-border/60 tabular-nums">
                    220K students
                  </span>
                </div>
              </div>
              <figcaption className="sr-only">
                CSW behavioral intelligence — surfacing engagement signals from real study sessions.
              </figcaption>
            </figure>
          </div>

          {/* hero stats strip */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border/60 rounded-2xl overflow-hidden border border-border/60">
            {[
              ["220K", "Active students"],
              ["5M", "Video minutes / mo"],
              ["45m–2h", "Avg. focus session"],
              ["3 layers", "Of behavioral signal"],
            ].map(([v, l]) => (
              <div key={l} className="bg-background px-6 py-6">
                <div className="font-display text-3xl lg:text-4xl text-gradient leading-none mb-2">{v}</div>
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-20 lg:py-28 border-t border-border/40" aria-labelledby="problem-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <span className="eyebrow-neon">The problem</span>
          <h2
            id="problem-title"
            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground max-w-3xl"
          >
            Institutions detect <em className="urgent">problems</em> too late.
          </h2>
          <div className="hairline w-32 mt-8 mb-10" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              ["Grades are lagging indicators.", "By the time a transcript reflects disengagement, the behavioral shift happened weeks earlier."],
              ["Disengagement is invisible.", "Existing systems weren't built to capture the day-to-day signals of how students actually study."],
              ["Students drift before they fail.", "Behavioral decline shows up well before academic decline — if you have the right signal layer."],
            ].map(([title, body]) => (
              <div key={title}>
                <h3 className="font-display italic text-xl lg:text-2xl text-foreground mb-3 leading-snug">
                  {title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE SURFACE */}
      <section className="py-20 lg:py-28 border-t border-border/40 bg-muted/10" aria-labelledby="signals-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="eyebrow-neon">What CSW surfaces</span>
            <h2
              id="signals-title"
              className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground"
            >
              Three layers of behavioral signal — <em className="urgent-cyan">explainable, attributable, reviewable.</em>
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              CSW does not diagnose, predict outcomes, or replace academic judgment. It surfaces
              patterns commonly associated with disengagement so advisors can decide what to do next.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Engagement signals", body: "Behavioral indicators within the platform — session cadence, focus patterns, study consistency, and participation rhythms over time." },
              { title: "Risk indicators", body: "Trend-based early-warning indicators when a student's behavioral pattern shifts in ways commonly associated with disengagement." },
              { title: "Advisor workflows", body: "Caseload triage, contextual notes, and explainable signal cards so success teams can reach out earlier with the right context." },
            ].map(({ title, body }) => (
              <article key={title} className="rounded-2xl border border-border bg-card p-7 hover:border-primary/40 transition-colors">
                <h3 className="font-display text-xl text-foreground mb-2">{title}</h3>
                <p className="text-muted-foreground leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-28 border-t border-border/40" aria-labelledby="how-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="eyebrow-neon">How it works</span>
            <h2 id="how-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground">
              A continuous loop — behavior surfaced for human review <em className="urgent">before</em> academic decline.
            </h2>
          </div>
          <ol className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              ["01", "Institution joins", "Students enter their institutional space inside CSW."],
              ["02", "Study & collaborate", "Sessions, focus timers, study groups, social signals."],
              ["03", "LMS context", "Canvas, Google Classroom, Moodle — assignments and deadlines for academic context."],
              ["04", "Signal engine", "Identifies behavioral patterns commonly associated with disengagement.", true],
              ["05", "Advisor surface", "At-risk indicators, trends, and contextual notes for human review."],
            ].map(([n, title, body, emphasize]) => (
              <li
                key={n as string}
                className={`pt-5 border-t-2 ${emphasize ? "" : "border-border/40"}`}
                style={emphasize ? { borderColor: "hsl(var(--primary))" } : undefined}
              >
                <div className={emphasize ? "eyebrow-neon mb-3" : "eyebrow mb-3"}>{n}</div>
                <h3 className={`font-display text-xl mb-2 ${emphasize ? "urgent" : "text-foreground"}`}>{title}</h3>
                <p className="text-sm text-muted-foreground leading-snug">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="py-20 lg:py-28 border-t border-border/40 bg-muted/10" aria-labelledby="for-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="eyebrow-neon">Built for the teams supporting students</span>
            <h2 id="for-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground">
              CSW for the institution that owns <em className="urgent-cyan">student success.</em>
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              CSW complements existing LMS and student success systems by adding a behavioral
              intelligence layer on top of the infrastructure you already have.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { href: "/for/universities/", title: "Universities", desc: "Retention, advising, and FYE program visibility across colleges and departments." },
              { href: "/for/colleges/", title: "Colleges", desc: "Community college and 4-year programs supporting transfer and persistence work." },
              { href: "/for/high-schools/", title: "High schools", desc: "Engagement visibility for counselors and student success staff in K-12 settings." },
              { href: "/for/research/", title: "Research", desc: "Anonymized behavioral data for learning-science labs and institutional research teams." },
            ].map(({ href, title, desc }) => (
              <a
                key={href}
                href={href}
                className="rounded-2xl border border-border bg-card p-6 hover:border-primary/50 transition-colors group flex flex-col"
              >
                <h3 className="font-display text-lg text-foreground mb-2">CSW for {title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{desc}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-4 group-hover:gap-2 transition-all">
                  Explore <ArrowRight className="w-4 h-4" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* LEARNING SUPPORT */}
      <section className="py-20 lg:py-28 border-t border-border/40" aria-labelledby="nd-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-14">
            <span className="eyebrow-neon">Learning support insights</span>
            <h2 id="nd-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground">
              Behavioral context for <em className="urgent-cyan">learning support teams.</em>
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              CSW surfaces behavioral engagement patterns — focus, routine, and consistency signals — that can
              support accessibility and student success initiatives. It is decision-support for human advisors,
              never diagnosis or clinical assessment.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                href: "/for/adhd-students/",
                title: "ADHD-related learning support",
                desc: "Behavioral focus and engagement insights — task-switching, session fragmentation, and consistency patterns — designed to support students with ADHD-related learning challenges.",
              },
              {
                href: "/for/autism-students/",
                title: "Structured routine support",
                desc: "Routine and engagement visibility tools designed to support structured learning environments for autistic students, with human advisors always in the loop.",
              },
            ].map(({ href, title, desc }) => (
              <a
                key={href}
                href={href}
                className="rounded-2xl border border-border bg-card p-6 hover:border-primary/50 transition-colors group flex flex-col"
              >
                <h3 className="font-display text-lg text-foreground mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{desc}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-4 group-hover:gap-2 transition-all">
                  Explore <ArrowRight className="w-4 h-4" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>



      {/* PRODUCT — DATA SURFACE */}
      <section className="py-20 lg:py-28 border-t border-border/40" aria-labelledby="product-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow-neon">The product</span>
            <h2 id="product-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground">
              Real-time behavioral <em className="urgent-cyan">intelligence.</em>
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              Three surfaces — one continuous signal loop. Engagement, regression, and individual baselines
              rendered for advisors and student success teams.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <ProductCard
              n="i."
              title="Cohort dashboard"
              items={["Active students by program", "Focus & session cadence", "Today's pattern breaks"]}
              mock={<BarMock />}
            />
            <ProductCard
              n="ii."
              title="Risk indicators"
              items={["Caseload triage list", "Severity scoring", "Recommended outreach context"]}
              mock={<ListMock />}
            />
            <ProductCard
              n="iii."
              title="Student baseline"
              items={["Individual behavioral baseline", "Focus & engagement trend", "Why a signal was surfaced"]}
              mock={<LineMock />}
            />
          </div>
        </div>
      </section>


      {/* WHY WE WIN — comparison */}
      <section className="py-20 lg:py-28 border-t border-border/40" aria-labelledby="vs-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mb-12">
            <span className="eyebrow-neon">Why CSW</span>
            <h2 id="vs-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground">
              Existing systems <em className="italic text-muted-foreground">record</em> education.<br />
              CSW <em className="urgent">understands</em> student behavior.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl">
            <div className="rounded-2xl border border-border p-8 bg-card">
              <div className="eyebrow mb-4">Traditional LMS</div>
              <p className="font-display text-2xl text-muted-foreground leading-snug">
                System of <em className="italic">record.</em>
              </p>
              <ul className="mt-6 text-sm text-muted-foreground space-y-1.5">
                <li>— Assignments</li>
                <li>— Grades</li>
                <li>— Attendance</li>
                <li>— Page views</li>
              </ul>
            </div>
            <div
              className="rounded-2xl p-8 border"
              style={{
                background: "linear-gradient(135deg, hsl(328 85% 14% / 0.6), hsl(193 95% 12% / 0.6))",
                borderColor: "hsl(var(--primary) / 0.4)",
                boxShadow: "var(--glow-primary)",
              }}
            >
              <div className="eyebrow-neon mb-4">CSW</div>
              <p className="font-display text-2xl leading-snug text-foreground">
                System of <em className="urgent-cyan">behavioral intelligence.</em>
              </p>
              <ul className="mt-6 text-sm text-foreground/85 space-y-1.5">
                <li>— Study behavior</li>
                <li>— Focus patterns</li>
                <li>— Engagement trends</li>
                <li>— Social learning signals</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* RESPONSIBLE AI */}
      <section className="py-20 lg:py-28 border-t border-border/40 bg-muted/10" aria-labelledby="trust-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-5">
              <ShieldCheck className="w-5 h-5 text-secondary" aria-hidden="true" />
              <span className="eyebrow-neon">Designed for human oversight</span>
            </div>
            <h2 id="trust-title" className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground mb-6">
              We surface patterns. <em className="urgent">Your team</em> makes the decisions.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              CSW does not diagnose mental health, predict academic outcomes, or recommend
              interventions on its own. Every signal is explainable, attributable to specific
              behavioral evidence, and intended to support advisor decision-making — not replace it.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Privacy-focused data handling, role-based access, and an auditable signal history are
              part of the platform from day one.
            </p>
          </div>
        </div>
      </section>

      {/* STUDENTS LAYER */}
      <section className="py-20 lg:py-28 border-t border-border/40" aria-labelledby="students-title">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="eyebrow-neon">For students</span>
              <h2 id="students-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground mb-6">
                The student layer that <em className="urgent-cyan">powers</em> the data.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                CSW's behavioral intelligence is informed by the same study platform students
                already use every day — live study streams, body doubling, focus timers, and group
                study rooms. The student experience stays free and consumer-friendly.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" variant="outline">
                  <a href="/students/">Explore the student experience</a>
                </Button>
                <Button asChild size="lg" variant="ghost">
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                    Open the student app →
                  </a>
                </Button>
              </div>
            </div>
            <figure className="relative">
              <DashboardMock />
              <figcaption className="sr-only">
                Behavioral signal network — focus, session, and social participation patterns.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-border/40 relative overflow-hidden" aria-labelledby="cta-title">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(50% 60% at 50% 100%, hsl(328 85% 60% / 0.18), transparent 70%)",
          }}
        />
        <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <h2 id="cta-title" className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-foreground mb-6 max-w-3xl mx-auto">
            Ready to see what your student engagement data is <em className="urgent">telling you?</em>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            We'll walk you through how CSW surfaces engagement signals, supports advisor workflows,
            and integrates with your existing LMS — using your institution's context.
          </p>
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-base glow-primary">
            <a href="/demo/">Request a demo<ArrowRight className="ml-2 h-4 w-4" /></a>
          </Button>
        </div>
      </section>
    </InstLayout>
  );
};

/* -------- Data-style mocks (themed via currentColor + semantic tokens) -------- */

function DashboardMock() {
  return (
    <div className="relative rounded-3xl border border-border/60 bg-card/60 backdrop-blur-sm shadow-2xl p-5 lg:p-6 text-foreground">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" aria-hidden="true" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Cohort · Live</span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground tabular-nums">Fall · Wk 06</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          ["12,438", "Active"],
          ["284", "At-risk"],
          ["94%", "Focus rate"],
        ].map(([v, l]) => (
          <div key={l} className="rounded-xl border border-border/60 bg-background/40 p-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">{l}</div>
            <div className="font-display text-2xl text-foreground tabular-nums">{v}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-border/60 bg-background/40 p-3 mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Engagement · 14d</span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-secondary">+ pattern break</span>
        </div>
        <div className="text-primary"><BarMock /></div>
      </div>
      <div className="rounded-xl border border-border/60 bg-background/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Baseline drift</span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground tabular-nums">−18% focus</span>
        </div>
        <div className="text-secondary h-16"><LineMock /></div>
      </div>
    </div>
  );
}

function ProductCard({
  n,
  title,
  items,
  mock,
}: {
  n: string;
  title: string;
  items: string[];
  mock: React.ReactNode;
}) {
  return (
    <article className="rounded-2xl border border-border bg-card p-6 flex flex-col hover:border-primary/40 transition-colors">
      <div className="eyebrow-neon mb-4">{n}</div>
      <div className="aspect-[4/3] rounded-xl border border-border/60 bg-background/40 mb-5 flex items-center justify-center overflow-hidden text-primary p-4">
        {mock}
      </div>
      <h3 className="font-display text-xl text-foreground mb-3">{title}</h3>
      <ul className="text-sm text-muted-foreground space-y-1.5">
        {items.map((i) => <li key={i}>— {i}</li>)}
      </ul>
    </article>
  );
}

function BarMock() {
  const bars = [60, 45, 72, 50, 80, 38, 65, 90, 55, 70, 48, 82];
  return (
    <svg viewBox="0 0 200 80" className="w-full h-full" aria-hidden="true">
      <line x1="0" y1="75" x2="200" y2="75" stroke="currentColor" strokeWidth="0.4" opacity="0.3" />
      {bars.map((h, i) => (
        <rect
          key={i}
          x={i * 16 + 4}
          y={75 - h * 0.7}
          width="10"
          height={h * 0.7}
          fill={i === 7 ? "hsl(var(--secondary))" : "currentColor"}
          opacity={i === 7 ? 1 : 0.45}
        />
      ))}
    </svg>
  );
}

function ListMock() {
  const rows: Array<[string, string, "high" | "med" | "low"]> = [
    ["A. Khan", "0.82", "high"],
    ["M. Silva", "0.71", "high"],
    ["L. Park", "0.58", "med"],
    ["J. Doe", "0.44", "med"],
    ["R. Patel", "0.31", "low"],
  ];
  return (
    <div className="w-full text-[11px] text-foreground/90">
      {rows.map(([n, s, t]) => (
        <div key={n} className="flex justify-between items-center py-1.5 border-b border-border/50 last:border-0">
          <span className="text-foreground">{n}</span>
          <span className="tabular-nums text-muted-foreground">{s}</span>
          <span
            className={`uppercase tracking-[0.2em] text-[9px] ${
              t === "high" ? "text-secondary" : "text-muted-foreground"
            }`}
          >
            {t}
          </span>
        </div>
      ))}
    </div>
  );
}

function LineMock() {
  return (
    <svg viewBox="0 0 200 100" className="w-full h-full" aria-hidden="true">
      <line x1="0" y1="90" x2="200" y2="90" stroke="currentColor" strokeWidth="0.4" opacity="0.3" />
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="0.6"
        opacity="0.4"
        strokeDasharray="2 2"
        points="0,50 200,50"
      />
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        points="0,30 20,28 40,35 60,32 80,45 100,50 120,62 140,58 160,70 180,75 200,82"
      />
      <circle cx="180" cy="75" r="2.5" fill="hsl(var(--secondary))" />
    </svg>
  );
}

function SignalsBackdrop() {
  const nodes: Array<[number, number]> = [
    [12, 30], [22, 55], [30, 22], [40, 70], [48, 40],
    [58, 18], [65, 60], [74, 35], [82, 72], [90, 28],
    [18, 80], [55, 85], [70, 12], [88, 55], [38, 90],
  ];
  const links: Array<[number, number]> = [
    [0,1],[0,2],[1,4],[2,4],[3,4],[4,5],[4,6],[5,7],[6,7],[6,8],
    [7,9],[8,11],[9,13],[10,1],[10,3],[11,6],[12,5],[12,9],[13,7],[14,3],[14,11],
  ];
  return (
    <div className="relative aspect-square rounded-3xl border border-border/60 bg-card/40 backdrop-blur-sm overflow-hidden text-primary">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true">
        {links.map(([a, b], i) => (
          <line
            key={i}
            x1={nodes[a][0]} y1={nodes[a][1]}
            x2={nodes[b][0]} y2={nodes[b][1]}
            stroke="currentColor" strokeWidth="0.18" opacity="0.55"
          />
        ))}
        {nodes.map(([x, y], i) => (
          <circle
            key={i}
            cx={x} cy={y}
            r={i % 4 === 0 ? 1.1 : 0.6}
            fill={i === 6 ? "hsl(var(--secondary))" : "currentColor"}
            opacity={i === 6 ? 1 : 0.85}
          />
        ))}
        <polyline
          fill="none" stroke="hsl(var(--secondary))" strokeWidth="0.3" opacity="0.85"
          points="0,72 10,68 20,70 30,62 40,64 50,55 60,50 70,42 80,40 90,32 100,28"
        />
      </svg>
      <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
        Signal network
      </div>
      <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.28em] text-muted-foreground tabular-nums">
        220K · live
      </div>
    </div>
  );
}

export default IndexB2B;
